import { Loader2, Save, Sparkles, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/modern/components/ui/dialog-shadcn";
import { Button } from "@/modern/components/ui/button-shadcn";
import { cn } from "@/modern/utils";
import { PromoteSummaryCard } from "./PromoteSummaryCard";

type PromoteConfirmDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isEditing: boolean;
  submitting: boolean;
  onConfirm: () => void;
};

/**
 * Stacked review dialog before Promote / Save Changes.
 * Shell: dialog.DialogContent + rounded.shell; z-index above FileDetailModal (+20).
 */
export const PromoteConfirmDialog = ({
  open,
  onOpenChange,
  isEditing,
  submitting,
  onConfirm,
}: PromoteConfirmDialogProps) => {
  const title = isEditing ? "Review changes" : "Review promotion";
  const description = isEditing
    ? "Confirm the details below before saving."
    : "Confirm the details below before promoting this file.";
  const primaryLabel = isEditing ? "Save Changes" : "Promote";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        overlayClassName="z-[calc(var(--swivu-bryntum-z-index)+20)]"
        className={cn(
          "z-[calc(var(--swivu-bryntum-z-index)+20)]",
          "flex max-h-[min(90vh,36rem)] w-full max-w-[calc(100%-2rem)] flex-col gap-0 overflow-hidden",
          "border-border/60 bg-background p-0 shadow-2xl",
          "rounded-[2rem] sm:max-w-lg",
        )}
      >
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <DialogDescription className="sr-only">{description}</DialogDescription>

        {/* dialog.DialogHeader — heading left, button.icon close right */}
        <div className="flex shrink-0 items-start gap-3 px-5 pt-5 sm:px-7">
          <div className="min-w-0 flex-1">
            <h2 className="text-xl font-normal tracking-tight text-foreground sm:text-2xl">{title}</h2>
            <p className="mt-0.5 text-sm font-normal text-muted-foreground">{description}</p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="icon"
            disabled={submitting}
            onClick={() => onOpenChange(false)}
            aria-label="Close"
            className="size-9 shrink-0 rounded-full border-input bg-muted text-foreground/80 shadow-sm hover:border-input hover:text-foreground"
          >
            <X className="size-4" />
          </Button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 pt-5 sm:px-7">
          <PromoteSummaryCard />
        </div>

        {/* Matched pills: outline-pill Cancel + primary Save */}
        <div className="flex shrink-0 items-center justify-end gap-2 px-5 pb-6 pt-5 sm:px-7">
          <Button
            type="button"
            variant="outline"
            disabled={submitting}
            onClick={() => onOpenChange(false)}
            className="h-9 rounded-full px-3 text-sm font-normal"
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={submitting}
            onClick={onConfirm}
            className="h-9 gap-1.5 rounded-full px-3 text-sm font-normal"
          >
            {submitting ? (
              <>
                <Loader2 className="size-3.5 animate-spin" />
                {isEditing ? "Saving..." : "Promoting..."}
              </>
            ) : (
              <>
                {isEditing ? <Save className="size-3.5" /> : <Sparkles className="size-3.5" />}
                {primaryLabel}
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
